import React, { Component, ErrorInfo, ReactNode } from "react";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Uncaught error:", error, errorInfo);
  }

  handleGoHome = () => {
    window.location.href = "/";
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div style={styles.container}>
          <div style={styles.card}>
            <div style={styles.iconWrapper}>⚠️</div>
            <h1 style={styles.title}>Something went wrong</h1>
            <p style={styles.message}>
              An unexpected error has occurred. We apologize for the
              inconvenience. Please try one of the options below.
            </p>

            <div style={styles.buttonGroup}>
              <button
                style={styles.primaryButton}
                onClick={this.handleGoHome}
                onMouseOver={(e) =>
                  Object.assign(
                    e.currentTarget.style,
                    styles.primaryButtonHover,
                  )
                }
                onMouseOut={(e) =>
                  Object.assign(e.currentTarget.style, styles.primaryButton)
                }
              >
                Go to Home
              </button>
              <button
                style={styles.secondaryButton}
                onClick={this.handleReload}
                onMouseOver={(e) =>
                  Object.assign(
                    e.currentTarget.style,
                    styles.secondaryButtonHover,
                  )
                }
                onMouseOut={(e) =>
                  Object.assign(e.currentTarget.style, styles.secondaryButton)
                }
              >
                Reload Page
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    height: "100vh",
    width: "100vw",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#f8f9fa",
    fontFamily: "'Inter', sans-serif",
  },
  card: {
    maxWidth: "480px",
    width: "90%",
    padding: "40px",
    borderRadius: "16px",
    backgroundColor: "#ffffff",
    boxShadow:
      "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
    textAlign: "center" as const,
  },
  iconWrapper: {
    fontSize: "48px",
    marginBottom: "24px",
  },
  title: {
    fontSize: "24px",
    fontWeight: "600",
    color: "#111827",
    marginBottom: "16px",
    marginTop: 0,
  },
  message: {
    fontSize: "16px",
    color: "#6b7280",
    marginBottom: "32px",
    lineHeight: "1.5",
  },
  buttonGroup: {
    display: "flex",
    gap: "16px",
    justifyContent: "center",
  },
  primaryButton: {
    padding: "12px 24px",
    backgroundColor: "#4f46e5",
    color: "#ffffff",
    border: "none",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "500",
    cursor: "pointer",
    transition: "all 0.2s ease",
    boxShadow: "0 4px 6px -1px rgba(79, 70, 229, 0.2)",
  },
  primaryButtonHover: {
    backgroundColor: "#4338ca",
    transform: "translateY(-1px)",
  },
  secondaryButton: {
    padding: "12px 24px",
    backgroundColor: "#ffffff",
    color: "#374151",
    border: "1px solid #d1d5db",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "500",
    cursor: "pointer",
    transition: "all 0.2s ease",
  },
  secondaryButtonHover: {
    backgroundColor: "#f9fafb",
    borderColor: "#9ca3af",
  },
};

export default ErrorBoundary;
